import { useState, useEffect, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useNavigate } from "react-router-dom";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { ArrowRight, Briefcase } from "lucide-react";
import api from "@/lib/api";
import { formatNumber, formatEuro, formatDate } from "@/lib/utils";

/* ── Soll-heute Berechnung ── */
function calcSollHeute() {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const year = today.getFullYear();

  // Valid weekdays: Mon=1, Tue=2, Thu=4, Fri=5, Sat=6
  const validDays = new Set([1, 2, 4, 5, 6]);

  // Fixed free periods (month is 0-indexed)
  function isFixedFree(m, d) {
    if (m === 0 && d >= 1 && d <= 6) return true;   // Jan 1-6
    if (m === 3 && d === 28) return true;             // Apr 28
    if (m === 5 && d === 28) return true;             // Jun 28
    if (m === 11 && d >= 24 && d <= 31) return true;  // Dec 24-31
    return false;
  }

  // Collect all potential Soll days
  const allSollDays = [];
  const dt = new Date(year, 0, 1);
  while (dt.getFullYear() === year) {
    if (validDays.has(dt.getDay()) && !isFixedFree(dt.getMonth(), dt.getDate())) {
      allSollDays.push(new Date(dt));
    }
    dt.setDate(dt.getDate() + 1);
  }

  // If more than 250, remove extra days starting after Jun 28
  let sollDays = [...allSollDays];
  if (sollDays.length > 250) {
    const jun28 = new Date(year, 5, 28);
    const afterJun28 = sollDays.filter(d => d > jun28);
    const beforeOrOn = sollDays.filter(d => d <= jun28);
    const excess = sollDays.length - 250;
    // Remove first 'excess' days from afterJun28
    const kept = afterJun28.slice(excess);
    sollDays = [...beforeOrOn, ...kept];
  }

  const total = sollDays.length;
  const ptsPerDay = 100000 / total;

  // Count Soll days up to and including today
  const todayTime = today.getTime();
  const elapsed = sollDays.filter(d => d.getTime() <= todayTime).length;

  const raw = Math.min(Math.max(elapsed * ptsPerDay, 0), 100000);
  const display = Math.round(raw / 100) * 100;

  return { raw, display, total, elapsed };
}

const ChartTip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-800 px-3 py-2 text-xs">
      <p className="text-zinc-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-mono text-sky-400">{formatNumber(p.value)} Pkt.</p>
      ))}
    </div>
  );
};

const CAT_COLORS = {
  Produktion: "#F97316", Administration: "#EF4444", Akquise: "#EAB308",
  Gesundheit: "#3B82F6", Ordnung: "#8B5CF6", Weiterbildung: "#10B981",
};

const BEST_TABS = [
  { key: "days", label: "Tage" },
  { key: "weeks", label: "Wochen" },
  { key: "months", label: "Monate" },
  { key: "years", label: "Jahre" },
];

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bestTab, setBestTab] = useState("days");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/dashboard").then((r) => { setData(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, []);

  const soll = useMemo(() => calcSollHeute(), []);

  if (loading) {
    return (
      <div className="p-4 md:p-8 space-y-4">
        {[1, 2, 3].map((i) => <div key={i} className="h-20 bg-zinc-900/50 animate-pulse" />)}
      </div>
    );
  }
  if (!data) return <div className="p-8 text-zinc-500">Fehler beim Laden.</div>;

  const dayPct = data.daily_goal > 0 ? Math.round((data.today_points / data.daily_goal) * 100) : 0;
  const weekRemain = Math.max(data.weekly_goal - data.week_points, 0);
  const yearRemain = Math.max(data.yearly_goal - data.year_total, 0);
  const workRemain = Math.round(Math.max(40 - (data.week_work_hours || 0), 0) * 10) / 10;

  const catEntries = Object.entries(data.last_30_categories || {}).filter(([, v]) => v > 0);
  const catMax = catEntries.length > 0 ? Math.max(...catEntries.map(([, v]) => v)) : 1;

  const bestItems = bestTab === "days"
    ? (data.best_days || []).slice(0, 5).map((d) => ({ label: formatDate(d.date), points: d.points }))
    : (data[`best_${bestTab}`] || []);

  return (
    <div className="p-4 md:p-8 space-y-4 max-w-4xl mx-auto" data-testid="dashboard-page">
      {/* Header */}
      <div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold text-white tracking-tight">
          Punktesystem
        </h1>
        <p className="text-zinc-600 text-sm mt-0.5">
          {new Date().toLocaleDateString("de-DE", { weekday: "long", day: "2-digit", month: "long", year: "numeric" })}
        </p>
      </div>

      {/* ── 1. Hauptblock ── */}
      <Card className="bg-card border-border" data-testid="year-total-card">
        <CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">
            Gesamtstand {new Date().getFullYear()}
          </p>
          <p className="font-mono text-3xl sm:text-4xl font-bold text-white tracking-tight">
            {formatNumber(data.year_total)}{" "}
            <span className="text-xl sm:text-2xl text-zinc-400 font-normal">Punkte</span>
          </p>
          <p className="text-sm text-zinc-400 mt-1">
            {data.yearly_goal > 0
              ? new Intl.NumberFormat("de-DE", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(
                  (data.year_total / data.yearly_goal) * 100
                )
              : "0,0"}{" "}
            % des Jahresziels erreicht
          </p>
          <p className="text-[11px] text-zinc-600 mt-0.5">
            ≈ {formatEuro(data.euro_value)} interner Gegenwert
          </p>
        </CardContent>
      </Card>

      {/* ── 2. Business ── */}
      <Card className="bg-card border-l-2 border-l-emerald-600 border-t-0 border-r border-b border-border" data-testid="business-card">
        <CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-emerald-500/70 mb-3">
            Echte Auftraege {new Date().getFullYear()}
          </p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-[9px] text-zinc-600 uppercase tracking-wider">Gewonnene Projekte</p>
              <p className="font-mono text-2xl font-bold text-white" data-testid="year-projekte">
                {data.year_projekte || 0}
              </p>
            </div>
            <div>
              <p className="text-[9px] text-zinc-600 uppercase tracking-wider">Auftragswert</p>
              <p className="font-mono text-2xl font-bold text-emerald-400" data-testid="year-auftragswert">
                {formatEuro(data.year_auftragswert || 0)}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── 3. Aktuelle Leistung (5 KPIs) ── */}
      <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="kpi-grid">
        {[
          { t: "Heute", v: formatNumber(data.today_points), s: `${dayPct}%`, c: data.today_points >= data.daily_goal ? "text-emerald-400" : "text-white" },
          { t: "Woche", v: formatNumber(data.week_points), s: `von ${formatNumber(data.weekly_goal)}`, c: data.week_points >= data.weekly_goal ? "text-emerald-400" : "text-white" },
          { t: "Monat", v: formatNumber(data.month_points), c: "text-white" },
          { t: "\u00D8 / Tag", v: formatNumber(data.daily_average), s: "Punkte", c: "text-zinc-300" },
          { t: "\u00D8 / Woche", v: formatNumber(data.weekly_average), s: "Punkte", c: "text-zinc-300" },
        ].map((k, i) => (
          <Card key={i} className="bg-card border-border">
            <CardContent className="p-3">
              <p className="text-[9px] uppercase tracking-wider text-zinc-500 mb-1">{k.t}</p>
              <p className={`font-mono text-lg font-medium ${k.c}`}>{k.v}</p>
              {k.s && <p className="text-[9px] text-zinc-600">{k.s}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── 4. Quick Action ── */}
      <button
        onClick={() => navigate("/heute")}
        className="w-full flex items-center justify-between p-3.5 bg-zinc-950 border border-zinc-800 hover:border-zinc-700 transition-colors"
        data-testid="quick-entry-btn"
      >
        <span className="text-sm text-zinc-300">Heutigen Eintrag erfassen</span>
        <ArrowRight className="h-4 w-4 text-zinc-500" />
      </button>

      {/* ── 5. Zielstatus ── */}
      <Card className="bg-card border-border">
        <CardContent className="p-4 space-y-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500">Zielstatus</p>
          {[
            { l: "Woche", v: data.week_points, m: data.weekly_goal, r: weekRemain, c: "bg-emerald-500" },
            { l: "Jahr", v: data.year_total, m: data.yearly_goal, r: yearRemain, c: "bg-sky-400" },
          ].map((g, i) => {
            const pct = g.m > 0 ? Math.min((g.v / g.m) * 100, 100) : 0;
            return (
              <div key={i}>
                <div className="flex justify-between items-baseline mb-1">
                  <span className="text-xs text-zinc-500">{g.l}</span>
                  <span className="font-mono text-xs text-zinc-400">
                    {g.r > 0 ? `Noch ${formatNumber(g.r)} Pkt.` : "Erreicht"}
                  </span>
                </div>
                <div className="h-1.5 bg-zinc-900 w-full">
                  <div className={`h-full transition-all duration-500 ${g.c}`} style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
          <div data-testid="soll-heute">
            <div className="flex justify-between items-baseline mb-1">
              <span className="text-xs text-zinc-500">Soll heute</span>
              <span className="font-mono text-xs text-zinc-400">
                ca. {formatNumber(soll.display)} Pkt.
              </span>
            </div>
            <div className="h-1.5 bg-zinc-900 w-full">
              <div className="h-full transition-all duration-500 bg-violet-500" style={{ width: `${Math.min((soll.raw / 100000) * 100, 100)}%` }} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ── 6. Arbeitszeit (Produktion + Admin + Akquise) ── */}
      <Card className="bg-card border-border" data-testid="arbeitszeit-card">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Briefcase className="h-3.5 w-3.5 text-zinc-600" />
              <p className="text-[10px] uppercase tracking-wider text-zinc-500">Arbeitszeit diese Woche</p>
            </div>
            <p className="font-mono text-sm text-zinc-400">
              {formatNumber(data.week_work_hours || 0)} <span className="text-zinc-600">/ 40 h</span>
            </p>
          </div>
          <div className="h-1.5 bg-zinc-900 w-full">
            <div
              className={`h-full transition-all duration-500 ${(data.week_work_reached_40) ? "bg-emerald-500" : "bg-amber-500"}`}
              style={{ width: `${Math.min(((data.week_work_hours || 0) / 40) * 100, 100)}%` }}
            />
          </div>
          <p className="text-[10px] text-zinc-600 mt-1.5">
            {workRemain > 0
              ? `Noch ${formatNumber(workRemain)} h bis 40-Stunden-Ziel`
              : "40-Stunden-Ziel erreicht"}
          </p>
          <p className="text-[8px] text-zinc-700 mt-0.5">
            Produktion + Administration + Akquise
          </p>
        </CardContent>
      </Card>

      {/* ── 7. Wochentrend (letzte 8 Wochen) ── */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">Letzte 8 Wochen</p>
          <div className="h-40" data-testid="weekly-trend">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.weekly_trend}>
                <XAxis dataKey="week" tick={{ fill: "#52525B", fontSize: 10 }} axisLine={false} tickLine={false} />
                <YAxis hide />
                <Tooltip content={<ChartTip />} />
                <ReferenceLine y={2000} stroke="#3F3F46" strokeDasharray="4 4" label={{ value: "2.000", fill: "#52525B", fontSize: 9, position: "left" }} />
                <Bar dataKey="points" fill="#38BDF8" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* ── 8. Kategorien 30 Tage ── */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-3">
            Kategorien – letzte 30 Tage
          </p>
          {catEntries.length > 0 ? (
            <div className="space-y-2.5">
              {catEntries
                .sort((a, b) => b[1] - a[1])
                .map(([name, hours]) => (
                  <div key={name}>
                    <div className="flex justify-between items-baseline mb-1">
                      <span className="text-xs text-zinc-400">{name}</span>
                      <span className="font-mono text-xs text-zinc-500">{hours} h</span>
                    </div>
                    <div className="h-2 bg-zinc-900 w-full">
                      <div
                        className="h-full transition-all duration-500"
                        style={{
                          width: `${(hours / catMax) * 100}%`,
                          backgroundColor: CAT_COLORS[name] || "#38BDF8",
                        }}
                      />
                    </div>
                  </div>
                ))}
            </div>
          ) : (
            <p className="text-zinc-600 text-sm py-4 text-center">Keine Daten</p>
          )}
        </CardContent>
      </Card>

      {/* ── 10. Beste Leistungen ── */}
      <Card className="bg-card border-border">
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] uppercase tracking-wider text-zinc-500">Beste Leistungen</p>
            <div className="flex gap-1" data-testid="best-tabs">
              {BEST_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setBestTab(t.key)}
                  className={`px-2 py-0.5 text-[10px] border transition-colors ${
                    bestTab === t.key
                      ? "bg-zinc-700 border-zinc-600 text-white"
                      : "bg-transparent border-zinc-800/60 text-zinc-600 hover:text-zinc-400"
                  }`}
                  data-testid={`best-${t.key}`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          {bestItems.length > 0 ? (
            <div className="space-y-1.5">
              {bestItems.map((d, i) => (
                <div key={i} className="flex justify-between items-center">
                  <span className="text-xs text-zinc-400">{d.label || formatDate(d.date)}</span>
                  <span className="font-mono text-xs text-white">{formatNumber(d.points)} Pkt.</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-zinc-600 text-sm py-2 text-center">Keine Daten</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
